"use client";

import Image from "next/image";
import { useId, useMemo, useState } from "react";

export type ShowcaseCard = {
  id: string;
  title: string;
  subtitle: string;
  imageSrc: string;
  imageAlt: string;
};

type InteractiveAppShowcaseProps = {
  cards: readonly ShowcaseCard[];
  className?: string;
};

/**
 * 4 panels (3 inactive + 1 active), gap-2 → 3 gaps × 8px = 24px.
 * lg: 3×56 + 280 + 24 = 472px. xl: 3×64 + 296 + 24 = 512px.
 * Hero column min-widths in HomePageContent remain sized for the original 5-panel layout.
 */
const GRID_TRACK_VARS =
  "[--col-inactive:3.5rem] [--col-active:17.5rem] xl:[--col-inactive:4rem] xl:[--col-active:18.5rem]";

const GRID_TRACK_TRANSITION =
  "transition-[grid-template-columns] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]";

const CONTENT_FADE =
  "transition-opacity duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]";

const PANEL_SHADOW = "shadow-md shadow-slate-900/10 dark:shadow-black/30";

/** Phone-shaped bezel; fixed dark surround in both themes so the device stands out. */
const PHONE_FRAME =
  "relative shrink-0 h-[calc(100%-8px)] max-w-full w-auto aspect-[9/19.5] rounded-[2rem] p-1.5 bg-slate-900 border border-slate-800 shadow-xl shadow-black/35";

const PHONE_SCREEN =
  "relative h-full w-full overflow-hidden rounded-[1.5rem] bg-slate-950";

const DESKTOP_PANEL_H = "h-[640px] lg:h-[690px] xl:h-[740px]";
const DESKTOP_ACCORDION = `min-h-[640px] lg:min-h-[690px] xl:min-h-[740px] ${DESKTOP_PANEL_H}`;

const DESKTOP_IMAGE_SIZES =
  "(min-width: 1280px) 296px, (min-width: 1024px) 280px, 320px";

function buildGridTemplateColumns(count: number, activeIndex: number): string {
  return Array.from({ length: count }, (_, i) =>
    i === activeIndex ? "var(--col-active)" : "var(--col-inactive)",
  ).join(" ");
}

function ShowcaseScreenshot({
  src,
  alt,
  priority,
  loading,
  sizes,
  objectFit = "cover",
}: {
  src: string;
  alt: string;
  priority?: boolean;
  loading?: "eager" | "lazy";
  sizes: string;
  objectFit?: "cover" | "contain";
}) {
  return (
    <Image
      src={src}
      alt={alt}
      fill
      sizes={sizes}
      className={
        objectFit === "cover"
          ? "object-cover object-top"
          : "object-contain object-top"
      }
      priority={priority}
      loading={loading}
    />
  );
}

export function InteractiveAppShowcase({ cards, className = "" }: InteractiveAppShowcaseProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const baseId = useId();

  const gridTemplateColumns = useMemo(
    () => buildGridTemplateColumns(cards.length, activeIndex),
    [cards.length, activeIndex],
  );

  if (cards.length === 0) return null;

  return (
    <div className={className}>
      {/* Desktop: grid-track accordion (stable total width, per-column resize) */}
      <div
        className={[
          "hidden lg:grid items-stretch",
          GRID_TRACK_VARS,
          GRID_TRACK_TRANSITION,
          DESKTOP_ACCORDION,
          "w-max max-w-full min-w-0 ml-auto gap-2",
        ].join(" ")}
        style={{ gridTemplateColumns }}
        role="tablist"
        aria-label="App features"
      >
        {cards.map((card, index) => {
          const isActive = index === activeIndex;
          const tabId = `${baseId}-tab-${card.id}`;
          const panelId = `${baseId}-panel-${card.id}`;

          return (
            <div
              key={card.id}
              role="tab"
              id={tabId}
              aria-selected={isActive}
              aria-controls={panelId}
              tabIndex={isActive ? 0 : -1}
              className={[
                DESKTOP_PANEL_H,
                "relative min-w-0 w-full h-full border cursor-pointer outline-none",
                isActive
                  ? "overflow-visible rounded-[2rem] border-0 bg-transparent shadow-none"
                  : [
                      PANEL_SHADOW,
                      "overflow-hidden rounded-[1.25rem]",
                      "border-slate-300/70 dark:border-slate-700/40",
                      "bg-slate-100 hover:bg-slate-200/90 dark:bg-slate-800/90 dark:hover:bg-slate-700/90",
                    ].join(" "),
                "focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
                "focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-950",
              ].join(" ")}
              onMouseEnter={() => setActiveIndex(index)}
              onFocus={() => setActiveIndex(index)}
              onClick={() => setActiveIndex(index)}
              onKeyDown={(e) => {
                if (e.key === "ArrowRight" || e.key === "ArrowDown") {
                  e.preventDefault();
                  setActiveIndex((i) => (i + 1) % cards.length);
                }
                if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
                  e.preventDefault();
                  setActiveIndex((i) => (i - 1 + cards.length) % cards.length);
                }
              }}
            >
              <div
                id={panelId}
                role="tabpanel"
                aria-labelledby={tabId}
                aria-hidden={!isActive}
                className={[
                  CONTENT_FADE,
                  "absolute inset-0 flex min-w-0 flex-col",
                  isActive ? "opacity-100 z-[1]" : "opacity-0 z-0 pointer-events-none",
                ].join(" ")}
              >
                <div className="relative flex flex-1 min-h-0 min-w-0 flex-col items-center justify-start overflow-visible py-0.5">
                  <div className={PHONE_FRAME}>
                    <div className={PHONE_SCREEN}>
                      <ShowcaseScreenshot
                        src={card.imageSrc}
                        alt={card.imageAlt}
                        sizes={DESKTOP_IMAGE_SIZES}
                        priority={index === 0}
                        loading={index === 0 ? "eager" : "lazy"}
                      />
                    </div>
                  </div>
                </div>
                {/*
                  Pin the title bar to a stable height so the image area above (flex-1) doesn't
                  change size mid-animation. Without this, the subtitle reflows from 2 lines to
                  1 line as the column widens, the bar shrinks, and the image grows downward —
                  giving the perceptual "horizontal then vertical" jerk.
                */}
                <div className="shrink-0 border-t border-slate-200/80 dark:border-slate-700/60 px-3 py-2.5 bg-white/95 dark:bg-slate-900/95">
                  <p className="text-sm font-semibold text-slate-900 dark:text-white leading-snug">
                    {card.title}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-400 leading-relaxed line-clamp-2 min-h-[2lh]">
                    {card.subtitle}
                  </p>
                </div>
              </div>

              <div
                aria-hidden={isActive}
                className={[
                  CONTENT_FADE,
                  "absolute inset-0 flex items-center justify-center",
                  isActive ? "opacity-0 z-0 pointer-events-none" : "opacity-100 z-[1]",
                ].join(" ")}
              >
                <span
                  className="text-xs lg:text-xs xl:text-base font-semibold tracking-wide text-slate-700 dark:text-white/90 drop-shadow-sm whitespace-nowrap"
                  style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}
                >
                  {card.title}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile: horizontal scroll cards */}
      <div
        className="lg:hidden -mx-4 px-4 overflow-x-auto overscroll-x-contain snap-x snap-mandatory scroll-px-4 pb-2 [scrollbar-width:thin]"
        aria-label="App features"
      >
        <div className="flex gap-4 w-max min-w-full pr-4">
          {cards.map((card, index) => (
            <article
              key={card.id}
              className="snap-center shrink-0 w-[min(85vw,16.5rem)] h-[520px] sm:h-[580px] flex flex-col overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-700/60 bg-slate-50 dark:bg-slate-900/80 shadow-md shadow-slate-900/5 dark:shadow-black/30"
            >
              <div className="relative flex flex-1 min-h-0 flex-col items-center justify-start overflow-visible py-0.5">
                <div className="relative shrink-0 h-[calc(100%-8px)] max-w-full w-auto aspect-[9/19.5] rounded-[1.75rem] p-1.5 bg-slate-900 border border-slate-800 shadow-xl shadow-black/35">
                  <div className="relative h-full w-full overflow-hidden rounded-[1.25rem] bg-slate-950">
                    <ShowcaseScreenshot
                      src={card.imageSrc}
                      alt={card.imageAlt}
                      sizes="85vw"
                      priority={index === 0}
                    />
                  </div>
                </div>
              </div>
              <div className="shrink-0 px-4 py-3 border-t border-slate-200/80 dark:border-slate-700/60">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{card.title}</h3>
                <p className="mt-1 text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                  {card.subtitle}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
